import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Lock, ShieldCheck, CheckCircle2, AlertTriangle, Sparkles, CreditCard, Landmark, Wallet, Smartphone, Check, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { loadRazorpay } from "../lib/razorpay";
import FemcareLogo from "../components/FemcareLogo";

/* ---------- UPI helpers ---------- */

// Popular Indian UPI apps. `hint` is passed to Razorpay so their checkout can
// short-circuit to the right intent when available.
const UPI_APPS = [
    { id: "phonepe",  label: "PhonePe",    short: "P", color: "#5F259F", suffix: "@ybl"   },
    { id: "gpay",     label: "Google Pay", short: "G", color: "#4285F4", suffix: "@oksbi" },
    { id: "paytm",    label: "Paytm",      short: "P", color: "#00BAF2", suffix: "@paytm" },
    { id: "bhim",     label: "BHIM",       short: "B", color: "#EA580C", suffix: "@upi"   },
    { id: "whatsapp", label: "WhatsApp",   short: "W", color: "#25D366", suffix: "@upi"   },
];

// Validates a Virtual Payment Address (a.k.a. UPI ID).
// Format: user@handle → 2-256 chars username, 2-64 chars handle, letters only in handle.
const VPA_REGEX = /^[a-zA-Z0-9.\-_]{2,256}@[a-zA-Z][a-zA-Z0-9]{1,63}$/;
const isValidVpa = (v) => VPA_REGEX.test((v || "").trim());

/**
 * /payment — mobile-app-initiated quick-pay page.
 *
 * Expected query params (all sent by the Femcare mobile app):
 *   plan       — human label to display, e.g. "Premium Annual"
 *   amount     — integer in the major unit (e.g. 999 for ₹999, 13 for $13)
 *   currency   — INR (default) or USD
 *   minor      — "1" if amount is already in paise/cents (rare, optional)
 *   userId     — app's internal user id (optional, saved with the order)
 *   returnUrl  — deep link the app registers, e.g.
 *                femcare://payment/success — we redirect there after 3s
 *
 * Flow:
 *   1) Land here with the params → summary + name/email form.
 *   2) User picks UPI (default), Card, Netbanking, or Wallet.
 *      - UPI: pick a UPI app quick-tile OR type a UPI ID (VPA) with live validation.
 *   3) POST /api/payments/quick-checkout → Razorpay Checkout opens preselected on the method.
 *   4) On payment.success → POST /api/payments/verify → success screen.
 *   5) After 3s countdown → window.location = returnUrl.
 */
export default function Payment() {
    const [params] = useSearchParams();

    // ---- Parse query params ----
    const q = useMemo(() => ({
        plan:      params.get("plan") || "",
        amount:    parseInt(params.get("amount") || "0", 10),
        currency:  (params.get("currency") || "INR").toUpperCase(),
        minor:     params.get("minor") === "1",
        userId:    params.get("userId") || null,
        returnUrl: params.get("returnUrl") || null,
        // Prefilled customer identity, passed by the mobile app for the
        // logged-in user. When these are all present we skip the name/email
        // form entirely and show only payment methods.
        name:      params.get("name") || "",
        email:     params.get("email") || "",
        phone:     params.get("phone") || "",
    }), [params]);

    const validParams = q.plan && q.amount > 0 && ["INR", "USD"].includes(q.currency);
    const symbol = q.currency === "INR" ? "₹" : "$";
    const displayAmount = q.minor ? Math.round(q.amount / 100) : q.amount;
    // UPI is India-only. Hide the UPI section for USD payments.
    const upiSupported = q.currency === "INR";
    // Per-field flags — the app can pre-fill any subset of {name, email, phone}.
    // Whatever it doesn't pass, we ask the user for on the payment screen.
    const needName  = !q.name;
    const needEmail = !q.email;
    const needPhone = !q.phone;
    const identityProvided = !needName && !needEmail && !needPhone;
    // We still hide the top-right badge unless we at least know the name.
    const showUserBadge = !!q.name;

    // ---- State ----
    const [form, setForm] = useState({ name: q.name, email: q.email, phone: q.phone });
    // Nothing is pre-selected — the user must pick a method (and, for UPI,
    // either a UPI app or type a UPI ID) before Pay is clickable.
    const [method, setMethod] = useState(null);
    const [upiApp, setUpiApp] = useState(null);
    const [vpa, setVpa] = useState("");
    // VPA verification state:
    //   idle       — no VPA typed yet
    //   checking   — request in flight to Razorpay
    //   valid      — Razorpay confirmed, customer_name populated
    //   invalid    — Razorpay rejected it
    //   unverified — the validation endpoint isn't enabled on this Razorpay
    //                account (or we hit a network error); we let the user
    //                proceed anyway and rely on the collect request itself.
    const [vpaCheck, setVpaCheck] = useState({ status: "idle", customerName: "", reason: "" });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [paidOrder, setPaidOrder] = useState(null);
    const [countdown, setCountdown] = useState(3);

    // Debounced VPA validation against Razorpay's /v1/payments/validate/vpa.
    // We call our backend proxy. When Razorpay confirms the VPA we show a
    // green tick + account holder's name. When Razorpay hasn't enabled the
    // validation feature on this account we treat the VPA as valid silently
    // (the actual collect request during payment is the final check).
    useEffect(() => {
        if (method !== "upi") {
            setVpaCheck({ status: "idle", customerName: "", reason: "" });
            return;
        }
        const trimmed = vpa.trim();
        if (!trimmed) {
            setVpaCheck({ status: "idle", customerName: "", reason: "" });
            return;
        }
        if (!isValidVpa(trimmed)) {
            setVpaCheck({ status: "invalid", customerName: "", reason: "Format looks off — should be name@bank" });
            return;
        }
        let cancelled = false;
        setVpaCheck({ status: "checking", customerName: "", reason: "" });
        const t = setTimeout(async () => {
            try {
                const { data } = await api.post("/payments/validate-vpa", { vpa: trimmed });
                if (cancelled) return;
                if (data.valid) {
                    // Whether Razorpay actually verified or just returned
                    // "not enabled → assume valid", we mark as valid.
                    setVpaCheck({ status: "valid", customerName: data.customer_name || "", reason: "" });
                } else {
                    setVpaCheck({ status: "invalid", customerName: "", reason: data.reason || "UPI ID not found" });
                }
            } catch {
                if (!cancelled) {
                    // Network / server error → still let the user proceed.
                    setVpaCheck({ status: "valid", customerName: "", reason: "" });
                }
            }
        }, 200);
        return () => { cancelled = true; clearTimeout(t); };
    }, [vpa, method]);

    // Sync form when the mobile app pushes new identity via query params
    // (e.g. user switched account mid-session and reopened the page).
    useEffect(() => {
        setForm({ name: q.name, email: q.email, phone: q.phone });
    }, [q.name, q.email, q.phone]);

    // If the user types a VPA, clear any app quick-select so we don't send mixed signals.
    const handleVpa = (v) => {
        setVpa(v);
        if (v && upiApp) setUpiApp(null);
    };
    // Tapping a UPI-app tile auto-derives that app's VPA from the user's
    // phone number (PhonePe default = <10-digit-mobile>@ybl, etc.). We use
    // this VPA to send a UPI **collect** request, which triggers a push
    // notification inside the chosen UPI app on BOTH iOS and Android.
    // The user can still edit the auto-filled VPA if their real one differs.
    // Tapping a UPI-app tile immediately launches the payment flow using
    // UPI intent — on Android the tile-tapped app opens directly; on iOS
    // Razorpay redirects into the app's custom scheme (e.g. phonepe://).
    // No extra Pay button click is needed for the tile flow.
    const handleApp = (appId) => {
        if (upiApp === appId) {
            // Tapping the already-selected tile clears the selection.
            setUpiApp(null);
            setVpa("");
            return;
        }
        setUpiApp(appId);
        // Any typed VPA is cleared — we're about to jump to intent flow.
        setVpa("");
        // Ensure "upi" method is set so submit logic branches correctly.
        setMethod("upi");
        // Fire the intent-flow payment immediately.
        startPayment({ intentApp: appId });
    };

    // ---- Post-success redirect back to app ----
    useEffect(() => {
        if (!paidOrder || !q.returnUrl) return;
        if (countdown <= 0) {
            window.location.href = q.returnUrl;
            return;
        }
        const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
        return () => clearTimeout(t);
    }, [paidOrder, countdown, q.returnUrl]);

    // ---- Core payment launcher (shared by tile-tap intent + Pay button collect) ----
    // opts.intentApp  — a UPI_APPS.id ("phonepe", "gpay", …) → launches that app's UPI intent
    // opts.vpaOverride — explicit VPA to use for collect flow (defaults to state `vpa`)
    // opts.methodOverride — force a specific method (defaults to state `method`)
    const startPayment = async (opts = {}) => {
        const intentApp = opts.intentApp || null;
        const useMethod = opts.methodOverride || method || (intentApp ? "upi" : null);
        const useVpa = (opts.vpaOverride ?? vpa ?? "").trim();

        if (!useMethod) {
            setError("Please choose a payment method.");
            return;
        }
        // Collect flow → we need a valid VPA. Intent flow → we don't.
        if (useMethod === "upi" && !intentApp) {
            if (!useVpa) {
                setError("Enter your UPI ID or tap a UPI app to continue.");
                return;
            }
            if (!isValidVpa(useVpa)) {
                setError("That UPI ID doesn't look right. Format: yourname@bank");
                return;
            }
        }

        setLoading(true); setError("");

        const ok = await loadRazorpay();
        if (!ok) {
            setError("Could not load payment gateway. Check your connection and try again.");
            setLoading(false);
            return;
        }

        try {
            const { data } = await api.post("/payments/quick-checkout", {
                plan_label: q.plan,
                amount: q.amount,
                currency: q.currency,
                minor_unit: q.minor,
                customer_name: form.name,
                customer_email: form.email,
                customer_phone: form.phone,
                user_id: q.userId,
                return_url: q.returnUrl,
            });

            // Restrict Razorpay to just the one method the user chose so their
            // internal method-picker screen never shows.
            const methodConfig = {
                upi:        useMethod === "upi",
                card:       useMethod === "card",
                netbanking: useMethod === "netbanking",
                wallet:     useMethod === "wallet",
                paylater:   false,
                emi:        false,
            };

            const prefill = { name: form.name, email: form.email, contact: form.phone };
            const rzpOptions = {
                key: data.key_id,
                amount: data.amount,
                currency: data.currency,
                name: "Femcare",
                description: q.plan,
                order_id: data.order_id,
                prefill,
                method: methodConfig,
                config: {
                    display: {
                        hide: [
                            ...(useMethod !== "card"       ? [{ method: "card" }]       : []),
                            ...(useMethod !== "netbanking" ? [{ method: "netbanking" }] : []),
                            ...(useMethod !== "wallet"     ? [{ method: "wallet" }]     : []),
                            ...(useMethod !== "upi"        ? [{ method: "upi" }]        : []),
                            { method: "paylater" },
                            { method: "emi" },
                            { method: "cardless_emi" },
                        ],
                    },
                },
                notes: {
                    method: useMethod,
                    upi_app: intentApp || "",
                    vpa: useVpa || "",
                },
                theme: { color: "#E91E8C" },
                handler: async (resp) => {
                    try {
                        const v = await api.post("/payments/verify", {
                            razorpay_order_id: resp.razorpay_order_id,
                            razorpay_payment_id: resp.razorpay_payment_id,
                            razorpay_signature: resp.razorpay_signature,
                        });
                        setPaidOrder(v.data.order);
                    } catch {
                        setError("Payment verification failed. If you were charged, please contact support@femcare.life with your payment id.");
                        setLoading(false);
                    }
                },
                modal: { ondismiss: () => setLoading(false) },
            };

            // ---- UPI flow selection ----
            if (useMethod === "upi") {
                prefill.method = "upi";
                if (intentApp) {
                    // Intent flow — Razorpay uses UPI_INTENT on Android to
                    // launch the specific app; on iOS it opens the app's
                    // custom scheme (phonepe://, tez://, paytmmp://).
                    const pkgMap = {
                        phonepe:  "com.phonepe.app",
                        gpay:     "com.google.android.apps.nbu.paisa.user",
                        paytm:    "net.one97.paytm",
                        bhim:     "in.org.npci.upiapp",
                        whatsapp: "com.whatsapp",
                    };
                    if (pkgMap[intentApp]) {
                        // Razorpay picks this up as a preferred UPI app on Android
                        // and pre-selects it, then fires the intent.
                        rzpOptions.upi = { flow: "intent", app_package_name: pkgMap[intentApp] };
                    } else {
                        rzpOptions.upi = { flow: "intent" };
                    }
                } else if (useVpa) {
                    // Collect flow — Razorpay pushes a collect request to the
                    // VPA and shows a "waiting for approval" loader.
                    prefill.vpa = useVpa;
                }
            }

            const rzp = new window.Razorpay(rzpOptions);
            rzp.on("payment.failed", (r) => {
                setError(r.error?.description || "Payment failed. Please try again.");
                setLoading(false);
            });
            rzp.open();
        } catch (err) {
            const d = err.response?.data?.detail;
            setError(typeof d === "string" ? d : "Could not start checkout. Please try again.");
            setLoading(false);
        }
    };

    // Pay button submit → collect flow (or non-UPI methods).
    const submit = (e) => {
        e.preventDefault();
        startPayment();
    };

    // ---- Invalid link ----
    if (!validParams) {
        return (
            <PageShell>
                <div className="bg-white border border-femcare-border rounded-3xl p-8 md:p-10 text-center shadow-xl" data-testid="payment-invalid">
                    <AlertTriangle className="w-10 h-10 text-femcare-pink mx-auto mb-4" />
                    <h1 className="font-serif text-2xl md:text-3xl text-femcare-purple mb-2">Invalid payment link</h1>
                    <p className="text-femcare-purple/70 text-sm leading-relaxed">
                        This payment page needs a plan, amount and currency to work.
                        Please open a fresh checkout from inside the Femcare app.
                    </p>
                    <p className="mt-6 text-xs text-femcare-purple/50">
                        Need help? <a href="mailto:support@femcare.life" className="text-femcare-pink underline">support@femcare.life</a>
                    </p>
                </div>
            </PageShell>
        );
    }

    // ---- Success ----
    if (paidOrder) {
        return (
            <PageShell>
                <div className="bg-white border border-femcare-border rounded-3xl p-8 md:p-10 text-center shadow-xl" data-testid="payment-success">
                    <div className="w-16 h-16 rounded-full bg-emerald-50 border-2 border-emerald-500 mx-auto mb-5 flex items-center justify-center">
                        <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                    </div>
                    <p className="text-xs uppercase tracking-[0.3em] text-emerald-600 font-medium mb-2">Payment successful</p>
                    <h1 className="font-serif text-3xl md:text-4xl text-femcare-purple leading-tight mb-3">
                        Thank you, {paidOrder.customer_name?.split(" ")[0] || "friend"}.
                    </h1>
                    <p className="text-femcare-purple/75 mb-6 leading-relaxed">
                        Your <b>{q.plan}</b> is active. A receipt is on its way to <b>{paidOrder.customer_email}</b>.
                    </p>

                    <div className="bg-femcare-petal/40 border border-femcare-border rounded-2xl p-4 text-left space-y-2 text-sm mb-6">
                        <Row label="Amount paid" value={<span className="font-serif text-lg text-femcare-pink">{symbol}{displayAmount.toLocaleString("en-IN")}</span>} />
                        <Row label="Plan" value={q.plan} />
                        <Row label="Receipt" value={<code className="text-xs">{paidOrder.receipt}</code>} />
                        {paidOrder.razorpay_payment_id && (
                            <Row label="Payment ID" value={<code className="text-xs">{paidOrder.razorpay_payment_id}</code>} />
                        )}
                    </div>

                    {q.returnUrl ? (
                        <>
                            <p className="text-sm text-femcare-purple/70 mb-3">
                                Sending you back to the Femcare app in <b>{countdown}s</b>…
                            </p>
                            <a
                                href={q.returnUrl}
                                className="w-full inline-flex items-center justify-center gap-2 btn-primary"
                                data-testid="return-to-app-btn"
                            >
                                Return to Femcare now
                            </a>
                        </>
                    ) : (
                        <p className="text-sm text-femcare-purple/70">You can safely close this window and reopen the Femcare app.</p>
                    )}
                </div>
            </PageShell>
        );
    }

    // ---- Payment form ----
    return (
        <PageShell>
            <div className="bg-white border border-femcare-border rounded-3xl shadow-xl overflow-hidden" data-testid="payment-page">
                {/* Order summary card */}
                <div className="bg-gradient-to-br from-femcare-pink via-femcare-accent to-femcare-purple text-white p-6 md:p-8">
                    <div className="flex items-start justify-between gap-3 mb-4">
                        <div className="flex items-center gap-2">
                            <FemcareLogo className="w-7 h-7 bg-white rounded-full p-0.5" />
                            <p className="text-sm md:text-base font-serif font-medium tracking-wide">Femcare</p>
                            <span className="hidden sm:inline text-[10px] uppercase tracking-[0.25em] text-pink-100/70 ml-1">Secure checkout</span>
                        </div>
                        {showUserBadge && (
                            <div className="flex items-center gap-2 bg-white/15 border border-white/25 backdrop-blur-sm rounded-full pl-1.5 pr-3 py-1" data-testid="payment-user-badge">
                                <span className="w-6 h-6 rounded-full bg-white text-femcare-pink flex items-center justify-center text-xs font-serif font-medium">
                                    {q.name.trim().charAt(0).toUpperCase() || "F"}
                                </span>
                                <span className="text-xs font-medium truncate max-w-[130px]" title={q.name}>
                                    {q.name.split(" ")[0]}
                                </span>
                            </div>
                        )}
                    </div>
                    <p className="text-xs uppercase tracking-wider text-pink-100/80 mb-1">You're paying for</p>
                    <h1 className="font-serif text-2xl md:text-3xl leading-tight mb-4" data-testid="payment-plan">{q.plan}</h1>
                    <div className="flex items-baseline gap-2">
                        <span className="font-serif text-5xl md:text-6xl font-medium" data-testid="payment-amount">
                            {symbol}{displayAmount.toLocaleString("en-IN")}
                        </span>
                        <span className="text-pink-100/80 text-sm uppercase tracking-wider" data-testid="payment-currency">{q.currency}</span>
                    </div>
                </div>

                {/* Form */}
                <form onSubmit={submit} className="p-6 md:p-8 space-y-5">
                    {/* Ask only for whatever the app did NOT pre-fill. */}
                    {needName && (
                        <div>
                            <label className="text-xs uppercase tracking-[0.2em] text-femcare-accent block mb-2 font-medium">Full name</label>
                            <input
                                required
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                placeholder="e.g. Priya Sharma"
                                className="w-full bg-femcare-cream border border-femcare-border rounded-xl px-4 py-3 focus:outline-none focus:border-femcare-pink"
                                data-testid="payment-name"
                            />
                        </div>
                    )}
                    {needEmail && (
                        <div>
                            <label className="text-xs uppercase tracking-[0.2em] text-femcare-accent block mb-2 font-medium">Email</label>
                            <input
                                type="email"
                                required
                                value={form.email}
                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                                placeholder="you@example.com"
                                className="w-full bg-femcare-cream border border-femcare-border rounded-xl px-4 py-3 focus:outline-none focus:border-femcare-pink"
                                data-testid="payment-email"
                            />
                            <p className="mt-1 text-xs text-femcare-purple/50">Your receipt will be sent here.</p>
                        </div>
                    )}
                    {needPhone && (
                        <div>
                            <label className="text-xs uppercase tracking-[0.2em] text-femcare-accent block mb-2 font-medium">
                                Phone number
                            </label>
                            <input
                                type="tel"
                                required
                                pattern="[0-9+\-\s()]{7,20}"
                                value={form.phone}
                                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                                placeholder="9000000000"
                                className="w-full bg-femcare-cream border border-femcare-border rounded-xl px-4 py-3 focus:outline-none focus:border-femcare-pink"
                                data-testid="payment-phone"
                            />
                            <p className="mt-1 text-xs text-femcare-purple/50">
                                Required for UPI collect requests and SMS payment receipts.
                            </p>
                        </div>
                    )}

                    {/* When everything came from the app, show a compact summary instead. */}
                    {identityProvided && (
                        <div className="bg-femcare-petal/40 border border-femcare-border rounded-xl px-4 py-3 text-sm" data-testid="payment-identity-summary">
                            <p className="text-xs uppercase tracking-[0.2em] text-femcare-accent mb-1 font-medium">Receipt to</p>
                            <p className="text-femcare-purple truncate">{q.email}</p>
                        </div>
                    )}

                    {/* ----------- Payment method selector ----------- */}
                    <div className="pt-2" data-testid="payment-methods">
                        <p className="text-xs uppercase tracking-[0.2em] text-femcare-accent mb-3 font-medium">Payment method</p>
                        <div className="flex flex-col gap-2">
                            {[
                                { id: "upi",        label: "UPI",         sub: "PhonePe · GPay · Paytm · Any UPI ID",  Icon: Smartphone, hidden: !upiSupported },
                                { id: "card",       label: "Cards",       sub: "Credit or Debit card",                  Icon: CreditCard },
                                { id: "netbanking", label: "Netbanking",  sub: "All major Indian banks",                Icon: Landmark,   hidden: !upiSupported },
                                { id: "wallet",     label: "Wallets",     sub: "Paytm · Mobikwik · Freecharge",          Icon: Wallet,     hidden: !upiSupported },
                            ].filter((m) => !m.hidden).map(({ id, label, sub, Icon }) => {
                                const active = method === id;
                                return (
                                    <button
                                        key={id}
                                        type="button"
                                        onClick={() => { setMethod(id); setError(""); }}
                                        data-testid={`method-${id}`}
                                        className={`relative border rounded-xl px-4 py-3 flex items-center gap-3 text-left transition ${
                                            active
                                                ? "border-femcare-pink bg-femcare-petal/60"
                                                : "border-femcare-border bg-white hover:border-femcare-pink/40"
                                        }`}
                                    >
                                        <span className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${
                                            active ? "bg-femcare-pink text-white" : "bg-femcare-petal/50 text-femcare-purple/70"
                                        }`}>
                                            <Icon className="w-4 h-4" />
                                        </span>
                                        <span className="flex-1">
                                            <span className={`block text-sm font-medium ${active ? "text-femcare-pink" : "text-femcare-purple"}`}>{label}</span>
                                            <span className="block text-[11px] text-femcare-purple/55 mt-0.5">{sub}</span>
                                        </span>
                                        <span className={`w-5 h-5 rounded-full border flex items-center justify-center shrink-0 ${
                                            active ? "border-femcare-pink bg-femcare-pink text-white" : "border-femcare-border bg-white"
                                        }`}>
                                            {active && <Check className="w-3 h-3" strokeWidth={3} />}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* ----------- UPI sub-panel ----------- */}
                    {method === "upi" && (
                        <div className="bg-femcare-petal/30 border border-femcare-border rounded-2xl p-4 space-y-4" data-testid="upi-panel">
                            <div>
                                <p className="text-xs uppercase tracking-[0.2em] text-femcare-accent mb-2 font-medium">Pay with UPI app</p>
                                <p className="text-[11px] text-femcare-purple/60 mb-3 leading-relaxed">
                                    Tap your UPI app — we'll open it directly on your phone.
                                </p>
                                <div className="grid grid-cols-5 gap-2" data-testid="upi-apps">
                                    {UPI_APPS.map((app) => {
                                        const active = upiApp === app.id;
                                        return (
                                            <button
                                                key={app.id}
                                                type="button"
                                                onClick={() => handleApp(app.id)}
                                                data-testid={`upi-app-${app.id}`}
                                                className={`relative rounded-xl p-2 flex flex-col items-center gap-1 border transition ${
                                                    active
                                                        ? "border-femcare-pink bg-white shadow-sm"
                                                        : "border-femcare-border bg-white hover:border-femcare-pink/40"
                                                }`}
                                            >
                                                <span
                                                    className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-serif font-medium"
                                                    style={{ background: app.color }}
                                                    aria-hidden
                                                >
                                                    {app.short}
                                                </span>
                                                <span className="text-[10px] leading-tight text-femcare-purple/80">{app.label}</span>
                                                {active && (
                                                    <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-femcare-pink text-white flex items-center justify-center">
                                                        <Check className="w-2.5 h-2.5" strokeWidth={3} />
                                                    </span>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className="flex items-center gap-3 text-[11px] text-femcare-purple/40 uppercase tracking-wider">
                                <span className="flex-1 border-t border-femcare-border" />
                                or
                                <span className="flex-1 border-t border-femcare-border" />
                            </div>

                            <div>
                                <label className="text-xs uppercase tracking-[0.2em] text-femcare-accent block mb-2 font-medium">
                                    Or enter UPI ID
                                </label>
                                <div className="relative">
                                    <input
                                        type="text"
                                        value={vpa}
                                        onChange={(e) => handleVpa(e.target.value)}
                                        placeholder="yourname@bank"
                                        autoComplete="off"
                                        inputMode="email"
                                        data-testid="upi-vpa-input"
                                        className={`w-full bg-white border rounded-xl px-4 py-3 pr-10 focus:outline-none transition ${
                                            !vpa ? "border-femcare-border focus:border-femcare-pink" :
                                            vpaCheck.status === "valid" ? "border-emerald-500 focus:border-emerald-600" :
                                            vpaCheck.status === "checking" ? "border-femcare-pink/50 focus:border-femcare-pink" :
                                            "border-red-400 focus:border-red-500"
                                        }`}
                                    />
                                    {vpa && (
                                        <span className="absolute right-3 top-1/2 -translate-y-1/2" data-testid="upi-vpa-status">
                                            {vpaCheck.status === "checking" ? (
                                                <Loader2 className="w-5 h-5 text-femcare-pink animate-spin" />
                                            ) : vpaCheck.status === "valid" ? (
                                                <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                                            ) : (
                                                <AlertTriangle className="w-5 h-5 text-red-400" />
                                            )}
                                        </span>
                                    )}
                                </div>
                                <p className="mt-1 text-[11px] text-femcare-purple/50">
                                    {!vpa && "Examples: priya@okhdfcbank · 9876543210@ybl · rahul@paytm"}
                                    {vpa && vpaCheck.status === "checking" && (
                                        <span className="text-femcare-pink">Checking UPI ID…</span>
                                    )}
                                    {vpa && vpaCheck.status === "invalid" && (
                                        <span className="text-red-600">{vpaCheck.reason || "UPI ID not found. Please double-check."}</span>
                                    )}
                                </p>
                            </div>
                        </div>
                    )}

                    {error && (
                        <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700" data-testid="payment-error">
                            {error}
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={
                            loading ||
                            !method ||
                            (method === "upi" && (
                                !vpa ||
                                vpaCheck.status === "checking" ||
                                vpaCheck.status === "invalid" ||
                                !isValidVpa(vpa)
                            ))
                        }
                        className="w-full btn-primary justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="payment-pay-btn"
                    >
                        <Lock className="w-4 h-4" />
                        {loading
                            ? "Processing…"
                            : !method
                                ? `Choose a payment method`
                                : method === "upi" && !vpa
                                    ? `Enter UPI ID or tap a UPI app above`
                                    : method === "upi" && vpaCheck.status === "checking"
                                        ? `Checking UPI ID…`
                                        : method === "upi" && vpaCheck.status === "invalid"
                                            ? `Enter a valid UPI ID`
                                            : `Pay now`}
                    </button>

                    <div className="flex items-center justify-center gap-2 text-[11px] text-femcare-purple/50 pt-1">
                        <ShieldCheck className="w-3.5 h-3.5" />
                        Powered by Razorpay · UPI · Cards · Netbanking · Wallets
                    </div>
                </form>
            </div>
        </PageShell>
    );
}

/* ---------- Presentational helpers ---------- */

const Row = ({ label, value }) => (
    <div className="flex justify-between items-center gap-4">
        <span className="text-femcare-purple/60 uppercase text-[10px] tracking-wider">{label}</span>
        <span className="text-femcare-purple text-right">{value}</span>
    </div>
);

const PageShell = ({ children }) => (
    <div className="min-h-screen bg-femcare-cream flex flex-col px-4 py-8">
        <div className="w-full max-w-md mx-auto flex-1">{children}</div>
        <p className="mt-6 text-[11px] text-femcare-purple/40 text-center">
            © 2026 Femcare Life Pvt. Ltd.
        </p>
    </div>
);
